import React, { useState, useEffect } from 'react';
import { Badge, Button, Spinner, Alert as ReactAlert } from 'reactstrap';
import { IconButton, Stack, Tooltip, TextField, InputAdornment } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { del } from '../hooks/apiFetch';
import { AI_UTILITIES } from '../../constants/aiUtilities';
import TeamSummary from './TeamSummary';
import './UserResults.css';
import { useNotification } from '../Notification/NotificationContext';

const SuccessfulResultsTab = ({
    successfulItems,
    setSuccessfulItems,
    loading,
    error,
    fetchSuccess,
    history,
    getFileIcon
}) => {
    const [userResults, setUserResults] = useState(successfulItems);
    const [filteredResults, setFilteredResults] = useState([]);
    const [deletingItems, setDeletingItems] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'lastModificatedDate', direction: 'desc' });
    const {
        showSuccess,
        showError
    } = useNotification();

    const formatDateTimeWithLineBreak = (date) => {
        if (!date) return '';
        const formattedDate = date.toLocaleDateString();
        const formattedTime = date.toLocaleTimeString();
        return (
            <div className="date-time-container">
                <div className="date-part">{formattedDate}, {formattedTime}</div>
            </div>
        );
    };

    const renderFileList = (fileNames) => {
        if (!fileNames) return null;

        const filesArray = typeof fileNames === 'string'
            ? fileNames.split(', ').filter(name => name && name.trim())
            : Array.isArray(fileNames)
                ? fileNames.filter(name => name && name.trim())
                : [];

        if (filesArray.length === 0) return null;

        const CHARACTER_LIMIT = 30;
        const MAX_FULL_DISPLAY = 1;

        const totalChars = filesArray.reduce((sum, str) => sum + str.length, 0);
        const shouldTruncate = filesArray.length > MAX_FULL_DISPLAY ||
            totalChars > CHARACTER_LIMIT;

        return (
            <div className="file-list-container">
                {filesArray.map((fileName, index) => {
                    const extension = fileName.split('.').pop();
                    const nameWithoutExtension = fileName.slice(0, -(extension.length + 1));
                    const displayName = shouldTruncate
                        ? `${nameWithoutExtension.substring(0, 30)}…`
                        : fileName.trim();

                    return (
                        <div key={index} className="file-item">
                            <Tooltip title={fileName.trim()}>
                                <div className="file-item-content has-tooltip">
                                    {getFileIcon(fileName.trim())}
                                    <span className="file-name text-data full-file-name ml-1">
                                        {displayName}
                                    </span>
                                </div>
                            </Tooltip>
                        </div>
                    )
                })}
            </div>
        );
    };

    useEffect(() => {
        setUserResults(successfulItems);
    }, [successfulItems]);

    useEffect(() => {
        let results = userResults;

        if (searchTerm) {
            const lowercasedSearch = searchTerm.toLowerCase();
            results = userResults.filter(item =>
                (item.promptName && item.promptName.toString().toLowerCase().includes(lowercasedSearch)) ||
                (item.id && item.id.toString().toLowerCase().includes(lowercasedSearch)) ||
                item.utility.toLowerCase().includes(lowercasedSearch) ||
                (item.originalFileNames && item.originalFileNames.toString().toLowerCase().includes(lowercasedSearch)) ||
                (item.resultFileNames && item.resultFileNames.toString().toLowerCase().includes(lowercasedSearch))
            );
        }

        if (sortConfig.key) {
            results = [...results].sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'date' || sortConfig.key === 'lastModificatedDate') {
                    aValue = aValue.getTime();
                    bValue = bValue.getTime();
                }

                if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        setFilteredResults(results);
    }, [userResults, searchTerm, sortConfig]);

    const fetchDeleteTask = async (inputIdentifier) => {
        await del(`/api/Task/DeleteTask/${encodeURIComponent(inputIdentifier)}`);
        showSuccess('Обработка успешно удалена');
    };

    const fetchLeaveTeam = async (inputIdentifier) => {
        await del(`/api/TaskFileAccess/DeleteTaskAccess/${encodeURIComponent(inputIdentifier)}`);
        showSuccess('Вы успешно покинули команду');
    };

    const handleDelete = async (inputIdentifier, role, teamMemberCount) => {
        let windowText;
        if (role === "Creator") {
            windowText = teamMemberCount === 1
                ? 'Вы уверены, что хотите удалить эту обработку?'
                : 'Вы уверены, что хотите удалить эту обработку? Она станет недоступной для всех членов команды.'
        } else {
            windowText = 'Вы уверены, что хотите покинуть команду? Эта обработка станет для Вас недоступной.'
        }

        if (!window.confirm(windowText)) {
            return;
        }

        setDeletingItems(prev => new Set(prev).add(inputIdentifier));

        try {
            if (role === "Creator") {
                await fetchDeleteTask(inputIdentifier);
            } else {
                await fetchLeaveTeam(inputIdentifier);
            }

            setSuccessfulItems(prev => prev.filter(item => item.inputIdentifier !== inputIdentifier));
        } catch (error) {
            console.error('Ошибка при удалении:', error);
            showError(error.message || 'Произошла ошибка при удалении');
        } finally {
            setDeletingItems(prev => new Set([...prev].filter(identifier => identifier !== inputIdentifier)));
        }
    };

    const handleViewResults = (utility, inputIdentifier, event) => {
        const isMiddleClick = event.button === 1;
        const isCtrlClick = event.ctrlKey || event.metaKey;

        let path;

        switch (utility) {
            case AI_UTILITIES.TRANSCRIBE.name:
                path = `${AI_UTILITIES.TRANSCRIBE.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.DIARIZE.name:
                path = `${AI_UTILITIES.DIARIZE.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.SUMMARIZE_RESUME.name:
                path = `${AI_UTILITIES.SUMMARIZE_RESUME.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.UNIVERSAL_SUMMARIZE.name:
                path = `${AI_UTILITIES.UNIVERSAL_SUMMARIZE.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.DEPERSONALIZE.name:
                path = `${AI_UTILITIES.DEPERSONALIZE.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.RETELLING.name:
                path = `${AI_UTILITIES.RETELLING.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.STANDARD_CONTROL_PSA.name:
                path = `${AI_UTILITIES.STANDARD_CONTROL_PSA.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.MARKET_PRICE_ANALYSIS.name:
                path = `${AI_UTILITIES.MARKET_PRICE_ANALYSIS.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.INVOICE_RECOGNIZER.name:
                path = `${AI_UTILITIES.INVOICE_RECOGNIZER.path}/${inputIdentifier}`;
                break;
            default:
                path = '/myresults';
        }

        if (isMiddleClick || isCtrlClick) {
            event.preventDefault();
            const url = `${window.location.origin}${path}`;
            window.open(url, '_blank');
        } else {
            history.push(path);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <span className="material-symbols-outlined sort-icon">unfold_more</span>;
        }
        return sortConfig.direction === 'asc'
            ? <span className="material-symbols-outlined sort-icon">arrow_upward</span>
            : <span className="material-symbols-outlined sort-icon">arrow_downward</span>;
    };

    const renderUserResultsTable = () => {
        return (
            <section className="table-responsive mt-0">
                <table className="table table-striped user-results-table">
                    <colgroup>
                        {filteredResults.some(item => item.hasUnreadChanges) && (
                            <col style={{ minWidth: '60px' }} />
                        )}
                        <col style={{ minWidth: '80px' }} />
                        <col style={{ minWidth: '100px' }} />
                        <col style={{ minWidth: '100px' }} />
                        <col style={{ minWidth: '150px' }} />
                        <col style={{ minWidth: '150px' }} />
                        <col style={{ minWidth: '80px' }} />
                        <col style={{ minWidth: '120px' }} />
                        <col style={{ minWidth: '120px' }} />
                        {filteredResults.some(item => item.comment) && (
                            <col style={{ minWidth: '150px' }} />
                        )}
                        <col style={{ minWidth: '120px' }} />
                    </colgroup>
                    <thead>
                        <tr>
                            {filteredResults.some(item => item.hasUnreadChanges) &&
                                <th className="text-center align-middle"></th>
                            }
                            <th
                                className="text-left align-middle sortable"
                                onClick={() => handleSort('id')}
                            >
                                <div className="header-content">
                                    <span className="text-data header-text">
                                        Id
                                    </span>
                                    <div className="sort-icon-container">
                                        {getSortIcon('id')}
                                    </div>
                                </div>
                            </th>
                            <th
                                className="text-left align-middle sortable"
                                onClick={() => handleSort('lastModificatedDate')}
                            >
                                <div className="header-content">
                                    <span className="text-data header-text">
                                        Время последнего изменения
                                    </span>
                                    <div className="sort-icon-container">
                                        {getSortIcon('lastModificatedDate')}
                                    </div>
                                </div>
                            </th>
                            <th
                                className="text-left align-middle sortable"
                                onClick={() => handleSort('date')}
                            >
                                <div className="header-content">
                                    <span className="text-data header-text">
                                        Дата и время создания
                                    </span>
                                    <div className="sort-icon-container">
                                        {getSortIcon('date')}
                                    </div>
                                </div>
                            </th>
                            <th
                                className="text-left align-middle sortable"
                                onClick={() => handleSort('utility')}
                            >
                                <div className="header-content">
                                    <span className="text-data header-text">
                                        Утилита
                                    </span>
                                    <div className="sort-icon-container">
                                        {getSortIcon('utility')}
                                    </div>
                                </div>
                            </th>
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Название промпта
                                </span>
                            </th>
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Команда
                                </span>
                            </th>
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Исходные Файлы
                                </span>
                            </th>
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Результаты
                                </span>
                            </th>
                            {filteredResults.some(item => item.comment) &&
                                <th className="text-left align-middle">
                                    <span className="text-data">
                                        Комментарий
                                    </span>
                                </th>
                            }
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Действия
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredResults.map(item => (
                            <tr key={item.id}>
                                {filteredResults.some(item => item.hasUnreadChanges) && (
                                    <td className="text-center align-middle">
                                        {item.hasUnreadChanges && (
                                            <span className="text-data">
                                                <Badge color="primary" pill className="unread-badge">
                                                    Новое
                                                </Badge>
                                            </span>
                                        )}
                                    </td>
                                )}
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {item.id}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {formatDateTimeWithLineBreak(item.lastModificatedDate)}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {formatDateTimeWithLineBreak(item.date)}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {item.utility}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {item.utility === "Универсальная суммаризация (техническая)"
                                            ? (item.promptName ? item.promptName : "Промпт был удалён")
                                            : "Стандартный промпт"
                                        }
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <TeamSummary teamMembers={item.teamMembers} currentUserRole={item.role} />
                                </td>
                                <td className="text-left align-middle">
                                    {renderFileList(item.originalFileNames)}
                                </td>
                                <td className="text-left align-middle">
                                    {renderFileList(item.resultFileNames)}
                                </td>
                                {filteredResults.some(item => item.comment) &&
                                    <td className="text-left align-middle">
                                        <span className="text-data">
                                            {item.comment}
                                        </span>
                                    </td>
                                }
                                <td className="text-left align-middle">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Tooltip title="Перейти">
                                            <IconButton
                                                onClick={(e) => handleViewResults(item.utility, item.inputIdentifier, e)}
                                                onMouseDown={(e) => {
                                                    if (e.button === 1) {
                                                        e.preventDefault();
                                                        handleViewResults(item.utility, item.inputIdentifier, e);
                                                    }
                                                }}
                                                size="small">
                                                <span className="material-symbols-outlined view-result-icon">
                                                    arrow_circle_right
                                                </span>
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Удалить">
                                            <IconButton
                                                onClick={() => handleDelete(item.inputIdentifier, item.role, item.teamMembers.length)}
                                                size="small"
                                                disabled={deletingItems.has(item.inputIdentifier)}
                                            >
                                                {deletingItems.has(item.inputIdentifier)
                                                    ? (
                                                        <span className="material-symbols-outlined icon delete-icon">
                                                            auto_delete
                                                        </span>
                                                    )
                                                    : (
                                                        <span className="material-symbols-outlined icon delete-icon">
                                                            delete
                                                        </span>
                                                    )}
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        );
    };

    return (
        <>
            <div className="search-container">
                <TextField
                    fullWidth
                    variant="standard"
                    placeholder="Поиск по id, утилите, файлам, названию промпта..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input text-input"
                    autoComplete="off"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon className="search-icon" />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>

            {loading ? (
                <div className="text-center my-5">
                    <Spinner color="primary" />
                    <p>Загрузка успешных обработок...</p>
                </div>
            ) : error ? (
                <ReactAlert color="danger" transition={{ timeout: 300 }}>
                    <p className="alert-heading">Ошибка при загрузке данных:</p>
                    <p className="black-color">{error}</p>
                    <br />
                    <Button
                        className="dt1520-button active-dt1520-button default-button view-button mt-10"
                        onClick={fetchSuccess}>
                        Попробовать снова
                    </Button>
                </ReactAlert>
            ) : filteredResults.length > 0 ? (
                renderUserResultsTable()
            ) : userResults.length > 0 ? (
                <ReactAlert color="warning" className="mt-2" transition={{ timeout: 300 }}>
                    По вашему запросу ничего не найдено
                </ReactAlert>
            ) : (
                <ReactAlert color="info" className="mt-2" transition={{ timeout: 300 }}>
                    Нет данных об успешных обработках
                </ReactAlert>
            )}
        </>
    );
};

export default SuccessfulResultsTab;